import _ from "underscore";
import DriveView from "../../../../DriveView";
import { Item as ListViewItem, generateChecklist, ListView } from "../../../framework/checklist";
import { Dialog } from "../../../framework/dialog";
import { IAddressSearch, populateAddressesRedux as fetchAddresses, populateDownloadedAddressesRedux } from "../../../../sync/common";
import Sync from "../../../../sync/sync";
import Api from "../../../../services/api";
import HosProcess from "../../../../services/hosProcess";
import DutyStatusLog, { IAddress } from "../../../../model/DutyStatusLog";
import * as logger from "../../../../utils/logger";
import { dispatcher, DispatcherEvents } from "../../../../utils/dispatcher";
import { convertMinutesToTimeString, setUsingReassignApi } from "../../../../utils/helpers";
import { getCurrentVehicle } from "../../../../utils/device";
import { dstCalculator } from "../../../../utils/date";
import { CancelablePromise as Promise } from "../../../../utils/cancelablePromise";
import { handleApi } from "../../../../utils/error";
import dayjs from "../../../../utils/dayjs";
import Spinner from "../../../../utils/spinner";
import { AuditType, auditSkip } from "../../../../utils/auditHelper";
import constants from "../../../../constants";
import { IViewLog } from "./logs";
import { extend } from "../../../../utils/vanilla";
import { PageLevel } from "../../../../store/mobile/types";
import { ICachedAddress } from "../../../../store/data/types";
import logsTemplate from "./logs.unassigned.tmpl";
import logTemplate from "./log.unassigned.tmpl";
import type { THosStatus } from "hos-tools";
export default class UnassignedLogsView extends DriveView<Backbone.Model> {

    protected strings = [
        "Saving in progress",
        "Assign to me"
    ];
    private readonly sync: Sync;
    private readonly api: Api;
    private readonly hosUtils: HosProcess;
    private readonly changeVehicle: boolean;
    private readonly templateLogs = _.template(logsTemplate);

    private filteredCollection: DutyStatusLog[];
    private displayCollection: IViewLog[];
    private logListView: ListView | null = null;
    private selectAll = false;
    private logsAddressCache = {};
    private populateLogsPromise: Promise<void>;
    private populateAddressesPromise: Promise<void>;
    private renderChecklistTimeout;
    private spinner: Spinner | undefined = new Spinner({
        top: "auto",
        left: "auto"
    });

    constructor (options) {
        super(options, {
            title: "Claim Unassigned Logs",
            activeSection: "hos",
            pageLevel: options && options.pageLevel || PageLevel.ONE
        });

        this.app = options.app;
        this.sync = this.app.sync;
        this.api = this.app.api;
        this.hosUtils = this.app.hosUtils;

        this.changeVehicle = options && options.changeVehicle || false;

        this.listenTo(dispatcher, DispatcherEvents.SyncHos, () => {
            if (!this.filteredCollection) {
                return;
            }

            this.populateLogs(this.loadLogs()).catch(handleApi);
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #selectAllNone": this.selectAllNone,
            "click #assignToSelf": this.warnAssignUnassigned,
            "click #assignToSkip": this.proceedWithAudit
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render () {
        this.populateLogs(this.loadLogs(), true).catch(handleApi);
        return this;
    }

    remove () {
        clearTimeout(this.renderChecklistTimeout);
        this.spinner && this.spinner.stop();
        this.spinner = undefined;
        this.cancelPendingPromises();
        this.logListView && this.logListView.remove();
        this.logListView = null;
        super.remove();
        return this;
    }

    private loadLogs (): DutyStatusLog[] {
        let sortedHOSLogs = this.app.dutyStatusLogService.getSortedHosLogs(
            this.app.dutyStatusLogService.getHOSLogs(false).filter(hosLog => hosLog.isUnassignedPageLog())
        );
        // Filter out logs that are not paired for the unassigned logs page
        return this.app.dutyStatusLogService.getAllPairedLogs(sortedHOSLogs);
    }

    private getFilteredLogs (logs: DutyStatusLog[]) {
        let currentVehicle = getCurrentVehicle() || { id: "NoDeviceId" };
        let displayStartDate = this.hosUtils.getRetentionStartDate();
        let displayEndDate = constants.MAX_DATE;
        let isDateExcluded = log => {
            let date = log.getDate();
            return (date < displayStartDate) || (displayEndDate && date.getTime() >= displayEndDate.getTime());
        };
        let logFilter = log => log.getDeviceId() !== currentVehicle.id || isDateExcluded(log) || !!log.requestedEdit;
        return logs.filter(log => !logFilter(log));
    }

    private populateLogs (collection: DutyStatusLog[], forceRender = false) {
        let filteredCollection = this.getFilteredLogs(collection);
        if (!forceRender && this.filteredCollection && this.app.dutyStatusLogService.areCollectionsEqual(this.filteredCollection, filteredCollection)) {
            return Promise.resolve<void>();
        }

        this.cancelPendingPromises();
        let getMinuteDuration = (log): number => this.app.dutyStatusLogService.getDuration(collection, log) / (1000 * 60);
        let newCollection: IViewLog[] = filteredCollection.map(log => log.clone());
        newCollection.forEach(log => {
            let currentDate = dstCalculator.toLocalDateTime(log.getDate());
            log._date = dayjs(currentDate).format("lll");
            log._duration = convertMinutesToTimeString(getMinuteDuration(log));
            log._address = log._address || "";
            log.className = "log";
        });

        let missingAddresses = this.getMissingAddresses(newCollection);
        this.populateAddressCache(missingAddresses, populateDownloadedAddressesRedux(this.app.store, missingAddresses), newCollection);

        this.displayCollection = newCollection;
        this.filteredCollection = filteredCollection;
        this.populateLogsPromise = this.renderTemplate().then(() => this.fetchMissingLocations(newCollection));
        return this.populateLogsPromise;
    }

    private renderTemplate() {
        return this.display(this.templateLogs({ debugMode: this.app.store.getState().data.debugMode, hasLogs: this.displayCollection.length > 0 }))
            .then(() => this.generateLogsCollectionList());
    }

    private generateLogsCollectionList() {
        clearTimeout(this.renderChecklistTimeout);

        if (!this.spinner) {
            return Promise.resolve();
        }

        let spinner = this.spinner;
        spinner.stop();

        return new Promise(resolve => {
            spinner.spin(document.getElementById("logs-list-container"));
            this.renderChecklistTimeout = setTimeout(() => {
                this.generateLogCollectionListImpl();
                spinner.stop();
                resolve();
            }, 1);
        });
    }

    private generateLogCollectionListImpl() {
        if (this.displayCollection.length) {
            this.logListView = generateChecklist({
                el: document.getElementById("logs-list-container"),
                items: this.displayCollection as any,
                labelTemplate: logTemplate,
                labelledBy: "title-text",
                isCheckable: true,
                selectCallback: this.clickUnassignedLog.bind(this),
                context: this,
                translator: this.translator
            });
        }
    }

    private clickUnassignedLog(listItem: ListViewItem) {
        let log: DutyStatusLog = new DutyStatusLog(listItem.attributes);
        let status: THosStatus = log.status;

        let pairedLog = status === "D"
            ? this.app.dutyStatusLogService.getNext(this.displayCollection, log, true)
            : this.app.dutyStatusLogService.getPrevious(this.displayCollection, log, true);

        if (pairedLog && pairedLog.status !== status) {
            let nextListLog = this.logListView && this.logListView.collection.get(pairedLog.id);
            let currentSelected = listItem.get("selected");
            nextListLog && nextListLog.set({ "selected": currentSelected });
        }

        this.toggleAssignButton();
    }

    private setLogAddress(log: IViewLog, logAddress) {
        let location = log.location || {} as IAddressLookupData;
        log.location = { address: logAddress, ...location };
        log._address = logAddress.formattedHosAddress ? logAddress.formattedHosAddress : "";
    }

    private getMissingAddresses(collection: DutyStatusLog[]) {
        let requestLocations: IAddressSearch[] = [];
        let startDate = this.hosUtils.getRetentionStartDate(2);

        collection.forEach(log => {
            let logId = log.id;
            if (log.location && log.location.location && log.isValidLocation() && !this.logsAddressCache.hasOwnProperty(logId) && log.getDate() >= startDate
            && (!log.location.address || !log.location.address.formattedAddress)) {
                requestLocations.push({
                    "id": logId,
                    "coordinate": log.location.location
                });
            }
        });
        return requestLocations;
    }

    private populateAddressCache(requestLocations: IAddressSearch[], addresses: IAddress[], collection: DutyStatusLog[]) {
        if (addresses && addresses.length) {
            logger.log(logger.Source.UI, "Got new addresses for unassigned HOS logs", this.app.userService.getCurrentUser());
            let addressCache = addresses.reduce((acc, addressEntity: IAddress, index) => {
                addressEntity && addressEntity.formattedHosAddress && (acc[requestLocations[index].id] = addressEntity);
                return acc;
            }, <ICachedAddress>{});

            this.logsAddressCache = extend(addressCache, (this.logsAddressCache || {}));
        }

        this.populateAddresses(collection);
    }

    private populateAddresses(collection: DutyStatusLog[]) {
        collection.forEach(log => {
            let logId = log.id;
            if (logId) {
                if (this.logsAddressCache.hasOwnProperty(logId) && this.logsAddressCache[logId].formattedHosAddress) {
                    let address = this.logsAddressCache[logId];
                    address && this.setLogAddress(log, address);
                }
            }
        });
    }

    private fetchMissingLocations(collection: DutyStatusLog[]) {
        let requestLocations = this.getMissingAddresses(collection);
        if (requestLocations.length > 0) {
            this.populateAddressesPromise = fetchAddresses(this.api, this.app.store, requestLocations, false)
                .then((addresses: IAddress[]) => {
                    this.populateAddressCache(requestLocations, addresses, collection);
                    // Update the rows in place. Rebuilding the page here would clear the logs the driver has already ticked.
                    collection.forEach(log => this.logListView?.collection.get(log.id)?.set({ _address: log._address, location: log.location }));
                })
                .catch(e => logger.error(logger.Source.ERROR, `Can't get locations. Error: ${ e ? e.message : "" }`, this.app.userService.getCurrentUser(), e));
        }
    }

    private selectAllNone() {
        if (!this.logListView) {
            return;
        }

        let buttonLabel = !this.selectAll
            ? "<svg class='svgIcon big bottom-margin' role='img' aria-label='Select none icon'><use xlink:href='#drive-cancel'></use></svg> None"
            : "<svg class='svgIcon big bottom-margin' role='img' aria-label='Select all icon'><use xlink:href='#drive-ok'></use></svg> All";

        this.selectAll = !this.selectAll;
        this.logListView.collection.setAll(this.selectAll);

        let selectAllButton = document.getElementById("selectAllNone");
        if (selectAllButton) {
            selectAllButton.innerHTML = buttonLabel;
        }
        this.toggleAssignButton();
    }

    private toggleAssignButton() {
        const selectedLogs = this.logListView?.collection.selected();
        const anyLogsSelected = !!(selectedLogs && selectedLogs.length);
        const assignBtn = <HTMLInputElement>document.getElementById("assignToSelf");
        assignBtn.disabled = !anyLogsSelected;
    }

    private warnAssignUnassigned() {
        const reassignedLogs = this.logListView?.collection.selected();
        if (!reassignedLogs || !reassignedLogs.length) {
            return;
        }

        let claimLogsDialog = new Dialog(
            "hos-claim-unassigned-logs",
            `<span>Your current availability may be affected.</span> <span>Are you sure you want to claim these logs?</span>`,
            this.translator,
            "Confirmation Required",
            [{
                id: "hosClaimCancel",
                text: "Cancel",
                className: "button_secondary",
                callback: () => {
                    claimLogsDialog.remove();
                }
            }, {
                id: "hosClaimOK",
                text: "OK",
                callback: () => {
                    claimLogsDialog.remove();
                    this.assignUnassigned(reassignedLogs);
                }
            }],
            {
                isNightMode: this.app.store.getState().data.nightMode
            }
        );

        void claimLogsDialog.open();
    }

    private assignUnassigned(reassignedLogs: ListViewItem[]) {
        let logs: DutyStatusLog[] = reassignedLogs.map(log => log.attributes);
        let assignBtn = <HTMLInputElement>document.getElementById("assignToSelf");
        let skipBtn = <HTMLInputElement>document.getElementById("assignToSkip");
        if (assignBtn && skipBtn) {
            assignBtn.disabled = true;
            skipBtn.disabled = true;
            assignBtn.innerHTML = this.getString("Saving in progress");
        }

        let handleError = (errorObject) => {
            handleApi(errorObject);

            if (assignBtn && skipBtn) {
                assignBtn.disabled = false;
                skipBtn.disabled = false;
                assignBtn.innerHTML = this.getString("Assign to me");
            }
        };


        return setUsingReassignApi(
            logs,
            this.app.userService.getCurrentUser(),
            false,
            this.sync,
            true
        )
        .then(() => this.proceedActionIfNecessary(this.getFilteredLogs(this.loadLogs()).length > 0))
        .catch(handleError);
    }

    private proceedWithAudit () {
        this.proceedActionIfNecessary(true);
    }

    private proceedActionIfNecessary (needsAudit: boolean) {
        needsAudit && auditSkip(this.app.api, AuditType.HosClaimUnassigned, this.changeVehicle ? "Vehicle change" : "Login");
        dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
    }

    private cancelPendingPromises () {
        this.populateLogsPromise && this.populateLogsPromise.cancel();
        this.populateAddressesPromise && this.populateAddressesPromise.cancel();
    }
}